import * as React from 'react';
import { compose, lifecycle, branch, renderComponent } from 'recompose';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps, Prompt } from 'react-router-dom';
import { isNil, toArray, random } from 'lodash';
import { InjectedIntlProps, injectIntl } from 'react-intl';

import NotFound from '../../NotFound';
import PageEditorHeader from './PageEditorHeader';
import ProgressBar, { IProgressBarProps } from '../../../components/ProgressBar';

import IMPComponent from '../../../components/interfaces/IMPComponent';

import * as pageEditorActions from './actions';
import * as pagesHistoryActions from '../actions';

import withActionsDispatch from '../../../hocs/withActionsDispatch';

import { getExpectedWordsCount } from '../../UserProfile/selectors';
import { getSelectedDate } from '../PagesHistoryCalendar/selectors';
import { makeGetMessagesByIdIncludes } from '../../LocaleSettings/selectors';
import { getEditablePage, getEditablePageWordsCount, getPageEditorFirstVisitState } from './selectors';

import EditorArea from '../../../components/EditorArea';
import { createSelector } from 'reselect';
import { EditingPage } from './reducer';
import styled from 'styled-components';
import moment from 'moment';
import { WritingIntervalsTracker } from './WritingIntervalsTracker';

const MESSAGES_BASE_PATH = 'containers.PagesHistory.PageEditor';

interface IPageEditorProps extends IMPComponent, InjectedIntlProps, IProgressBarProps {
  selectedDate: string;
  savePageEditorEditablePage: typeof pageEditorActions.savePageEditorEditablePage;
  cancelPageEditorEdit: typeof pageEditorActions.cancelPageEditorEdit;
  updatePageEditorEditablePage: typeof pageEditorActions.updatePageEditorEditablePage;
  setPageEditorEditablePageById: typeof pageEditorActions.setPageEditorEditablePageById;
  loadPagesHistory: typeof pagesHistoryActions.loadPagesHistory;
  editablePage: EditingPage;
  textPlaceholder: string;
}

interface IMatchProps {
  pageId?: string;
}

const getTextPlaceholder = createSelector(
  getPageEditorFirstVisitState,
  makeGetMessagesByIdIncludes(`${MESSAGES_BASE_PATH}.firstVisitPlaceholder`),
  makeGetMessagesByIdIncludes(`${MESSAGES_BASE_PATH}.placeholder`),
  (firstTimeVisit: boolean, firstTime: any[], regular: any[]) => {
    firstTime = toArray(firstTime);
    regular = toArray(regular);
    let placeholder: string = regular[random(0, regular.length - 1)];

    if (firstTimeVisit) {
      placeholder = firstTime.join('\n');
    }

    return placeholder;
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  flex-grow: 1;
`;

const HeaderContainer = styled.div`
  margin: 0;
`;

const enhance = compose<IPageEditorProps, any>(
  withRouter,
  injectIntl,
  connect(
    state => {
      return {
        selectedDate: getSelectedDate(state),
        editablePage: getEditablePage(state),
        expectedWordsCount: getExpectedWordsCount(state),
        actualWordsCount: getEditablePageWordsCount(state),
        textPlaceholder: getTextPlaceholder(state),
      };
    },
    pageEditorActions,
  ),
  lifecycle({
    componentDidMount() {
      const {
        match: {
          params: { pageId },
        },
        setPageEditorEditablePageById,
      } = this.props as any;

      if (!isNil(pageId)) {
        setPageEditorEditablePageById(pageId);
      }
    },
  }),
  withActionsDispatch({
    onMount: [pageEditorActions.startPageEditorSaga()],
    onUnmount: [pageEditorActions.cancelPageEditorSaga()],
  }),
  branch(({ editablePage }) => isNil(editablePage), renderComponent(NotFound)),
);

interface PageEditorState {
  isBlocking: boolean;
}

class PageEditor extends React.PureComponent<IPageEditorProps & RouteComponentProps<IMatchProps>, PageEditorState> {
  private readonly textAreaRef: React.RefObject<HTMLTextAreaElement>;
  private writingIntervalsHandler: WritingIntervalsTracker;

  constructor(props: any) {
    super(props);

    this.writingIntervalsHandler = new WritingIntervalsTracker();
    this.textAreaRef = React.createRef();
    this.state = {
      isBlocking: false,
    };
  }

  public componentDidMount(): void {
    if (this.textAreaRef.current) {
      this.textAreaRef.current.focus();
    }

    this.syncEditingState();
  }

  public componentDidUpdate(): void {
    this.syncEditingState();

    if (!this.writingIntervalsHandler.isActive()) {
      this.writingIntervalsHandler.start(this.props.editablePage.writingIntervals);
    }
  }

  public componentWillUnmount(): void {
    this.props.cancelPageEditorEdit();
    this.writingIntervalsHandler.stop();
  }

  public render() {
    const {
      selectedDate,
      editablePage: { dateId: editablePageId, text: content },
      expectedWordsCount,
      actualWordsCount,
    } = this.props;

    return (
      <Container>
        <HeaderContainer>
          <PageEditorHeader
            isEditing={!isNil(editablePageId)}
            onSave={this.handleSave}
            onCancel={this.cancelEditor}
            selectedDate={selectedDate}
            expectedWordsCount={expectedWordsCount}
            actualWordsCount={actualWordsCount}
            canSave={Boolean(content)}
          />
        </HeaderContainer>
        <HeaderContainer>
          <ProgressBar expectedWordsCount={expectedWordsCount} actualWordsCount={actualWordsCount} />
        </HeaderContainer>
        <EditorArea
          onChange={this.handleInputChange}
          placeholder={this.props.textPlaceholder}
          value={content}
          textAreaRef={this.textAreaRef}
        />

        <Prompt message={this.handleCancel} />
      </Container>
    );
  }

  private syncEditingState(): void {
    if (this.props.editablePage.isChanged && !this.state.isBlocking) {
      this.setState({
        isBlocking: true,
      });
    }
  }

  private handleInputChange = ({ target: { value } }: any) => {
    this.writingIntervalsHandler.updateActivityTime(moment().toDate());

    this.props.updatePageEditorEditablePage({
      text: value,
      writingIntervals: this.writingIntervalsHandler.getWritingIntervals(),
    });

    this.setState({
      isBlocking: value.length > 0,
    });
  };

  private handleSave = () => {
    this.setState(
      {
        isBlocking: false,
      },
      () => {
        this.props.savePageEditorEditablePage();
      },
    );
  };

  private cancelEditor = () => {
    if (this.handleCancel()) {
      this.setState(
        {
          isBlocking: false,
        },
        () => {
          this.props.cancelPageEditorEdit();
        },
      );
    }
  };

  private handleCancel = (): boolean => {
    const {
      intl: { formatMessage },
    } = this.props;

    let canCancel = true;

    if (this.state.isBlocking) {
      canCancel = confirm(
        formatMessage({
          id: 'containers.PagesHistory.PageEditor.PageEditorHeader.exitPromptMessage',
        }),
      );
    }

    return canCancel;
  };
}

export default enhance(PageEditor);



// WEBPACK FOOTER //
// ./src/containers/PagesHistory/PageEditor/index.tsx